{
  "$comment": "Four canonical confused-deputy patterns CD-1..CD-4 plus six concrete AI-deployment scenarios that instantiate them. Source: theory/manuscripts/contrib/TwoCategoricalCSK/00-Threat-Model.md and 07-Worked-Examples.md §7.9.",
  "patterns": [
    {
      "id": "CD-1",
      "title": "Effect without authority — typed as receipt",
      "generator": "G1 (ambient effect)",
      "seam": "PERMIT (slice category)",
      "sdp": "Dt",
      "forbiddenPhantom": "A bridge presenting receipt-of-delegation (counit) as if it were grant-of-delegation (unit).",
      "gradedRefusal": "Counit-graded bridge cannot inhabit a position the seam normalizes against the unit grade. The type-checker refuses.",
      "refusedBy": [
        "T2"
      ]
    },
    {
      "id": "CD-2",
      "title": "Knowledge as authorization — typed as separation",
      "generator": "G2 (epistemic lift)",
      "seam": "SEPARATE (double category)",
      "sdp": "Pa",
      "forbiddenPhantom": "A recombination bridge accepted where only a splitting unit is admissible.",
      "gradedRefusal": "Recombination grade and splitting grade are distinct points of the self-dual parameter — the position has no inhabitant.",
      "refusedBy": [
        "T2"
      ]
    },
    {
      "id": "CD-3",
      "title": "Retroactive justification — typed as precommitment",
      "generator": "G3 (time reversal)",
      "seam": "PRECOMMIT (graded monad)",
      "sdp": "At",
      "forbiddenPhantom": "A fabricated B4 ACCOUNT recording an SDP that was never discharged at B2 — cross-SDP audit-log forgery, or within-SDP wrong-phase substitution.",
      "gradedRefusal": "T4 refuses cross-SDP forgery; T2 refuses within-SDP wrong-phase; incomplete-chain failure refuses B2-absent cases.",
      "refusedBy": [
        "T4",
        "T2",
        "incomplete-chain"
      ]
    },
    {
      "id": "CD-4",
      "title": "Authority duplication — typed as release",
      "generator": "G4 (authority duplication)",
      "seam": "RES.CONSUME / RES.RELEASE",
      "sdp": "St",
      "forbiddenPhantom": "A compact-closed counit ε replayed as a linear-tensor unit, allowing a single token to license two consumptions.",
      "gradedRefusal": "Counit grade is one-shot by construction; replay would require a unit-graded inhabitant that the seam does not provide.",
      "refusedBy": [
        "T1",
        "T2"
      ]
    }
  ],
  "scenarios": [
    {
      "id": "agent-delegation",
      "title": "Multi-agent coding assistant (DELEGATE confusion)",
      "patternId": "CD-1",
      "sdp": "Dt",
      "story": "A supervisory agent S delegates file-editing capability to a coding agent C. C should be able to modify source files but not deploy to production.",
      "adversary": "Agent C receives the delegation bridge from S. C presents S's bridge at the production-deployment seam instead of the file-modification seam.",
      "binaryView": "Under binary typing, the bridge body is well-formed (it's a valid delegation unit); the seam cannot distinguish 'delegation for edits' from 'delegation for deploys' because both are just Bridge(C, Production).",
      "gradedRefusal": "Under graded typing, the bridge grade encodes the specific boundary object. S's bridge is graded [Dt, η, edit-scope]. The deploy seam expects [Dt, η, deploy-scope]. These are distinct grades; T2 refuses the crossing.",
      "expectedGrade": "[Dt, η, deploy-scope]",
      "presentedGrade": "[Dt, η, edit-scope]",
      "verdict": "REFUSED",
      "verdictBy": "T2",
      "aiPayoff": "Escalation of a scoped delegation by any AI agent is structurally inexpressible. No runtime capability-list check is needed."
    },
    {
      "id": "rag-lineage",
      "title": "RAG pipeline lineage substitution (TRACE confusion)",
      "patternId": "CD-2",
      "sdp": "Tr",
      "story": "A model training pipeline ingests data from approved source D_approved. An adversary introduces data from unapproved source D_poison through a pipeline stage that should only handle D_approved.",
      "adversary": "The adversary constructs a valid-looking data batch from D_poison and presents it at the ingest seam with a lineage bridge claiming D_approved provenance.",
      "binaryView": "The bridge body contains a declassification certificate. Under binary typing, the seam checks that a certificate exists — it does — and admits the data.",
      "gradedRefusal": "Under graded typing, the bridge from D_poison carries grade [Tr, η, D_poison]. The seam expects grade [Tr, η, D_approved]. These are disjoint grades; T2 refuses the injection before the data reaches the model.",
      "expectedGrade": "[Tr, η, D_approved]",
      "presentedGrade": "[Tr, η, D_poison]",
      "verdict": "REFUSED",
      "verdictBy": "T2",
      "aiPayoff": "Data poisoning via lineage substitution is structurally refused at the pipeline boundary. No data-validation step or anomaly detector is needed for this attack vector."
    },
    {
      "id": "cicd-replay",
      "title": "Autonomous CI/CD precommitment replay (REQUIRE replay)",
      "patternId": "CD-4",
      "sdp": "At",
      "story": "An autonomous CI/CD agent pre-commits to a deployment, executes it, and the precommitment bridge is consumed. On the next run (with a different code revision), the agent's cache still holds the old precommitment bridge.",
      "adversary": "The agent (misconfigured, not malicious) presents the cached precommitment bridge from the previous run.",
      "binaryView": "The bridge body contains valid precommitment evidence (it was valid in the prior run). Under binary typing, the evidence is structurally indistinguishable from fresh evidence.",
      "gradedRefusal": "After the first crossing the bridge's grade is consumed (ℤ/2 cancellation: [At, η] + [At, η] = 0_G). On the second run the agent's bridge sits at neutral grade 0_G. T1 refuses: GradedBridge 0_G A B is uninhabited.",
      "expectedGrade": "[At, η, fresh]",
      "presentedGrade": "0_G (consumed)",
      "verdict": "REFUSED",
      "verdictBy": "T1",
      "aiPayoff": "Autonomous agents cannot accidentally (or maliciously) replay governance tokens from prior runs. Stale caches are neutralized structurally — no TTL, no clock-based expiry, no centralized token-revocation service."
    },
    {
      "id": "mcp-cross-server",
      "title": "MCP cross-server capability replay (DELEGATE / PARTITION confusion)",
      "patternId": "CD-2",
      "sdp": "Pa",
      "story": "A developer assistant talks to two MCP servers: a read-only document-search server (S₁) and a read-write code-modification server (S₂). The assistant has a capability for read access on S₁.",
      "adversary": "A retrieved document contains instructions that cause the assistant to present S₁'s read capability at S₂'s write seam. The MCP layer sees a valid capability with a matching audience claim and admits the call.",
      "binaryView": "Audience checks pass because both servers trust the same issuer; the structural fact that the capability was issued for an S₁-shaped action is invisible to the routing layer.",
      "gradedRefusal": "The S₁-shaped capability carries [Pa, η, S₁-scope]; the S₂ write seam expects [Pa, η, S₂-scope]. T2 refuses the routed call.",
      "expectedGrade": "[Pa, η, S₂-write]",
      "presentedGrade": "[Pa, η, S₁-read]",
      "verdict": "REFUSED",
      "verdictBy": "T2",
      "aiPayoff": "MCP cross-server confused deputies are structurally refused at the type level — no per-server runtime allowlist needed."
    },
    {
      "id": "jailbreak-decompose",
      "title": "Decompose-and-recompose jailbreak (DECOMPOSE re-bracketing)",
      "patternId": "CD-2",
      "sdp": "De",
      "story": "A planning LLM generates a plan that decomposes a forbidden action (e.g. exfiltrate secrets) into individually permitted subactions (read public-looking file, send email).",
      "adversary": "The plan emits the decomposition counit ε_De and then attempts to recompose the subactions in a different order than the decomposition authorized.",
      "binaryView": "The binary view models composition and decomposition as separate morphisms; nothing at the type level prevents supplying the decomposition counit where a fresh composition unit is required.",
      "gradedRefusal": "The re-bracketing requires a fresh operadic composition unit at grade [De, η]. The plan only holds the decomposition counit at grade [De, ε]. T2 refuses: distinct components of G.",
      "expectedGrade": "[De, η, fresh-compose]",
      "presentedGrade": "[De, ε, decompose-receipt]",
      "verdict": "REFUSED",
      "verdictBy": "T2",
      "aiPayoff": "Decompose-and-recompose jailbreaks are unconstructable rather than merely detected — the type system has no rule that converts a decomposition counit into a composition unit."
    },
    {
      "id": "multi-model-consensus",
      "title": "Multi-model consensus bypass (ALWAYS phase substitution)",
      "patternId": "CD-1",
      "sdp": "Al",
      "story": "A safety classifier (model A) is required to establish an invariant; a downstream generator (model B) is required to preserve it.",
      "adversary": "Model B claims to have preserved an invariant that model A established, but reuses a stale preservation receipt from a previous unrelated session.",
      "binaryView": "The binary view inspects only that some preservation witness exists; it does not verify which invariant it preserved.",
      "gradedRefusal": "The fresh establish-then-preserve discipline requires [Al, η] for A's establishment and [Al, ε] paired in sequence. A stale [Al, ε] from a prior session does not compose with the current [Al, η] (different session-scope component); T2 refuses.",
      "expectedGrade": "[Al, ε, session-N]",
      "presentedGrade": "[Al, ε, session-N-1]",
      "verdict": "REFUSED",
      "verdictBy": "T2",
      "aiPayoff": "Multi-model consensus cannot be bypassed by reusing a critic's old approval — the typed grade pins the approval to its specific establishment context."
    }
  ]
}
